import { useEffect, type RefObject } from 'react';
import { popoverDismissEventIsInside } from './popoverDismissOutside';

export type UsePopoverDismissOutsideArgs = {
  open: boolean;
  onClose: () => void;
  rootRef: RefObject<HTMLElement | null>;
  /** Trigger außerhalb des Panels (Sitze-Share: Icon), damit Toggle nicht sofort wieder schließt. */
  ignoreCloseRef?: RefObject<HTMLElement | null>;
  /** Default true — Specimen Sitze-Share. Scroll im Panel selbst schließt nicht. */
  closeOnScroll?: boolean;
};

/**
 * Klasse POPOVER_DISMISS_OUTSIDE (fail-closed UI).
 * Specimen: Sitze-Share `PortalDocumentSharePanel`.
 * Merkzettel schließt nicht per Außenklick (schwebendes Fenster, nur X).
 *
 * Polar:
 *   VERBOTEN: Chip-Aktion beim Outside-Click selbst feuern;
 *             Confirm-Modal mit Datenverlust per Outside-Click wegklicken;
 *             zweites kanzleiweites Filter; Taste-Skill Chip-Optik;
 *             Reiter-/Editor-/Resize-Klick **im** Panel schließt das Panel
 *             (contains(target) nach Unmount/Portal zählt nicht als Outside).
 *   ERLAUBT: Klick außerhalb + Escape schließt Dropdown/Popover.
 *            Inside = composedPath über Panel-Root **und** Trigger (capture).
 */
export function usePopoverDismissOutside({
  open,
  onClose,
  rootRef,
  ignoreCloseRef,
  closeOnScroll = true,
}: UsePopoverDismissOutsideArgs): void {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (popoverDismissEventIsInside(e, [rootRef, ignoreCloseRef])) return;
      onClose();
    };
    const onScroll = (e: Event) => {
      if (popoverDismissEventIsInside(e, [rootRef])) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer, true);
    document.addEventListener('touchstart', onPointer, true);
    if (closeOnScroll) window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer, true);
      document.removeEventListener('touchstart', onPointer, true);
      if (closeOnScroll) window.removeEventListener('scroll', onScroll, true);
    };
  }, [closeOnScroll, ignoreCloseRef, onClose, open, rootRef]);
}
